"use client";

import { useState, useEffect } from "react";
import { Pencil, Phone, Save } from "lucide-react";
import { useContact } from "./context/ContactContext";

export default function ContactAdminPage() {

  const { getContact, updateContact, loading } = useContact();
  const [edit, setEdit] = useState(false);

  const [contact, setContact] = useState({
    website: "",
    email: "",
    phone: "",
    hours: "",
    address: "",
    enquiries: "",
    instagram: "",
    twitter: "",
    facebook: ""
  });

  useEffect(() => {
    fetchContact();
  }, []);

  const fetchContact = async () => {
    const res = await getContact();
    if (res?.status === 200 || res?.status === true) {
      setContact(res.data || res.contact || res);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Phone: strip anything that's not a digit as the user types
    if (name === "phone") {
      const digitsOnly = value.replace(/[^0-9]/g, "");
      setContact((prev) => ({
        ...prev,
        phone: digitsOnly
      }));
      return;
    }

    setContact((prev) => ({
      ...prev,
      [name]: value
    }));
  };
const allowedEmailDomains = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com"];

const isValidEmail = (value) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!emailRegex.test(value)) return false;

  const domain = value.split("@")[1]?.toLowerCase();
  return allowedEmailDomains.includes(domain);
};
  // Requires a proper domain: something.something (e.g. manaprint.in, manaprint.com)
const domainRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/\S*)?$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const phoneRegex = /^[6-9]\d{9}$/;

const handleSave = async () => {

  if (!phoneRegex.test(contact.phone)) {
    alert("Phone number must be 10 digits");
    return;
  }

  if (!isValidEmail(contact.email)) {
    alert(`Email must be from one of: ${allowedEmailDomains.join(", ")}`);
    return;
  }

  if (!isValidEmail(contact.enquiries)) {
    alert(`Business Enquiries email must be from one of: ${allowedEmailDomains.join(", ")}`);
    return;
  }

  if (contact.website && !domainRegex.test(contact.website)) {
    alert("Enter a valid Website (e.g. manaprint.in)");
    return;
  }

  if (contact.instagram && !domainRegex.test(contact.instagram)) {
    alert("Enter a valid Instagram link (e.g. https://www.instagram.com/manaprint_india/)");
    return;
  }

  if (contact.twitter && !domainRegex.test(contact.twitter)) {
    alert("Enter a valid X (Twitter) link");
    return;
  }

  if (contact.facebook && !domainRegex.test(contact.facebook)) {
    alert("Enter a valid Facebook link");
    return;
  }

  const res = await updateContact(contact);

  if (res?.status === 200 || res?.status === true) {
    alert("Contact Updated Successfully");
    setEdit(false);
    fetchContact();
  } else {
    alert(res.message || "Update failed");
  }
};

  return (
    <div className="p-6 bg-gray-50 min-h-screen">

      {/* Card */}
      <div className="bg-white rounded-xl shadow border border-gray-200 p-6">

        <div className="grid md:grid-cols-2 gap-6">

          <Field label="Website" name="website" edit={edit} value={contact.website} onChange={handleChange} />

          <Field label="Email" name="email" edit={edit} value={contact.email} onChange={handleChange} />

          <Field label="Phone" name="phone" edit={edit} value={contact.phone} onChange={handleChange} />

          <Field label="Support Hours" name="hours" edit={edit} value={contact.hours} onChange={handleChange} />

          <Field label="Business Enquiries" name="enquiries" edit={edit} value={contact.enquiries} onChange={handleChange} />

          <Field label="Address" name="address" edit={edit} value={contact.address} onChange={handleChange} />

        </div>

        {/* Social Links */}
        <div className="mt-8 border-t pt-6">

          <h2 className="text-lg font-semibold text-teal-700 mb-4">
            Social Media Links
          </h2>

          <div className="grid md:grid-cols-3 gap-6">

            <Field label="Instagram" name="instagram" edit={edit} value={contact.instagram} onChange={handleChange} />

            <Field label="X" name="twitter" edit={edit} value={contact.twitter} onChange={handleChange} />

            <Field label="Facebook" name="facebook" edit={edit} value={contact.facebook} onChange={handleChange} />

          </div>

        </div>

        {/* Buttons */}
        <div className="flex justify-end mt-8">

          {!edit ? (
            <button
              onClick={() => setEdit(true)}
              className="flex items-center gap-2 bg-teal-600 text-white px-5 py-2 rounded-lg hover:bg-teal-700 transition"
            >
              <Pencil size={16} />
              Edit Contact
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 bg-teal-600 text-white px-5 py-2 rounded-lg  transition"
            >
              <Save size={16} />
              {loading ? "Saving..." : "Save Changes"}
            </button>
          )}

        </div>

      </div>

    </div>
  );
}

function Field({ label, name, edit, value, onChange }) {
  return (
    <div className="flex flex-col">

      <label className="text-sm font-medium text-gray-600 mb-1">
        {label}
      </label>

      {edit ? (
        <input
          type="text"
          name={name}
          value={value || ""}
          onChange={onChange}
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:outline-none"
        />
      ) : (
        <p className="text-gray-800 break-all bg-gray-50 px-3 py-2 rounded-lg">
          {value || "-"}
        </p>
      )}

    </div>
  );
}